import {
  CalendarOutlined,
  CloseOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { Space, Tag, TagProps, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { FC, ReactNode } from 'react'
import {
  Subscription,
  SubscriptionStatus,
} from '../../../models/membership/Subscription'
import { useAppSelector } from '../../../store/hooks'
import { selectSubscriptionById } from '../../../store/membership/SubscriptionReducer'
import {
  currencyFormat,
  currencySymbolOf,
} from '../../../utils/currencyFormatter'
import Coupon from '../coupon/Coupon'
import MembershipProfile from '../membership-profile/MembershipProfile'

const { Text } = Typography
type Props = SBModelComponent<Subscription> & {
  plan?: boolean
  price?: boolean
  renewalDate?: boolean
  coupon?: boolean
  profiles?: boolean
  createdAt?: boolean
}

const MembershipSubscriptionAvatar: FC<Props> = ({
  id,
  model,
  plan,
  price,
  renewalDate,
  coupon,
  profiles,
  createdAt,
}) => {
  const item = model ? model : useAppSelector(selectSubscriptionById(id!))

  if (!item) {
    return <></>
  }

  const RenewalTag: FC = () => {
    if (!renewalDate) return <></>
    const configForStatus: Partial<
      Record<
        SubscriptionStatus,
        {
          title: ReactNode
          color: TagProps['color']
          tagChildren: ReactNode
        }
      >
    > = {
      cancelled: {
        title: 'Cancelled',
        color: 'red',
        tagChildren: (
          <>
            <CloseOutlined /> Cancelled
          </>
        ),
      },
      to_cancel: {
        title: 'Scheduled for cancellation upon renewal',
        color: 'red',
        tagChildren: (
          <>
            <StopOutlined /> {dayjs(item.renewalDate).format('DD/MM')}
          </>
        ),
      },
    }
    if (item.status in configForStatus) {
      const { title, color, tagChildren } = configForStatus[item.status]!
      return (
        <Tooltip title={title}>
          <Tag color={color} children={tagChildren} />
        </Tooltip>
      )
    }
    return (
      <Tooltip
        title={`Next renewal - ${dayjs(item.renewalDate).format('DD/MM/YYYY')}`}
      >
        <Tag color="orange">
          <SyncOutlined /> {dayjs(item.renewalDate).format('DD/MM')}
        </Tag>
      </Tooltip>
    )
  }

  return (
    <Space className="w-full" orientation="vertical" size={20}>
      <Space className="align-items-center w-full">
        {plan && <Tag color="blue">{item.plan?.en.name}</Tag>}
        {price && (
          <Tag color="green">
            {currencyFormat(
              item.plan?.price,
              true,
              true,
              false,
              currencySymbolOf(item.plan?.program?.currency)
            )}
          </Tag>
        )}
        <RenewalTag />

        {coupon && <Coupon.Avatar showDetails id={item.coupon} />}
        {createdAt && (
          <Tooltip title={'Submission date'}>
            <Tag
              color={
                dayjs().diff(dayjs(item.createdAt), 'hour') < 24
                  ? 'blue'
                  : dayjs().diff(dayjs(item.createdAt), 'hour') < 48
                    ? 'orange'
                    : 'red'
              }
            >
              <CalendarOutlined /> {dayjs(item.createdAt).format('DD/MM/YY')}
            </Tag>
          </Tooltip>
        )}
      </Space>
      {profiles && (
        <Space className="w-full" orientation="vertical">
          <Text type="secondary">Associated members :</Text>
          <Space className="align-items-center w-full" wrap>
            {item.membershipProfiles?.map((profile) => (
              <MembershipProfile.Avatar id={profile} key={profile} />
            ))}
          </Space>
        </Space>
      )}
    </Space>
  )
}

type MembershipSubscriptionAvatarType = {
  Avatar: typeof MembershipSubscriptionAvatar
}

export { MembershipSubscriptionAvatar, type MembershipSubscriptionAvatarType }
